import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, ArrowLeft, Sparkles, Loader2, FileText, Navigation } from 'lucide-react';
import { motion } from 'framer-motion';
import RouteBuilderStep from '../components/RouteBuilderStep';

const SubmissionReview = () => {
  const { venueId, submissionId } = useParams();
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showRouteBuilder, setShowRouteBuilder] = useState(false);
  const [areas, setAreas] = useState([]);
  const [customRoutes, setCustomRoutes] = useState([]);
  const [brandColor, setBrandColor] = useState('#1e3a5f');
  const [venue, setVenue] = useState(null);

  useEffect(() => {
    checkReadiness();
    fetchAreas();
    fetchVenue();
  }, [submissionId]);

  const checkReadiness = async () => {
    try {
      const response = await api.get(`/submissions/${submissionId}/readiness`);
      setReadiness(response.data);
      // Load existing custom routes if any
      if (response.data?.submission?.custom_routes) {
        setCustomRoutes(response.data.submission.custom_routes);
      }
    } catch (error) {
      toast.error('Failed to check submission readiness');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAreas = async () => {
    try {
      const response = await api.get(`/areas/venue/${venueId}`);
      // Sort by order
      const sortedAreas = (response.data || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setAreas(sortedAreas);
    } catch (error) {
      console.error('Failed to fetch areas:', error);
    }
  };
  
  const fetchVenue = async () => {
    try {
      const response = await api.get(`/venues/${venueId}`);
      setVenue(response.data);
      // Load existing brand color or use default
      setBrandColor(response.data.brand_color_1 || '#1e3a5f');
    } catch (error) {
      console.error('Failed to fetch venue:', error);
    }
  };
  
  const handleSaveRoutes = async (routes) => {
    try {
      await api.patch(`/submissions/${submissionId}/routes`, {
        custom_routes: routes
      });
      setCustomRoutes(routes);
      setShowRouteBuilder(false);
    } catch (error) {
      throw error;
    }
  };

  const handleSubmitAndGenerate = async () => {
    if (!readiness?.is_ready) {
      toast.error('Please complete all requirements before submitting');
      return;
    }

    setProcessing(true);

    try {
      // Save brand color to venue before generating guide
      await api.patch(`/venues/${venueId}`, {
        brand_color_1: brandColor || '#1e3a5f'
      });
      
      // Submit the submission to lock it
      await api.post(`/submissions/${submissionId}/submit`);
      
      // Generate the guide
      await api.post(`/submissions/${submissionId}/generate-guide`);
      
      toast.success('Your experience guide has been generated!');
      navigate(`/guide/${submissionId}`);
    } catch (error) {
      console.error('Guide generation error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong generating your guide');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(`/venue/${venueId}`)}
          className="mb-6 rounded-full"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Venue
        </Button>

        <div className="bg-card rounded-xl border border-border/50 shadow-lg p-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="review-title">
            Complete Your Assessment
          </h1>
          <p className="text-muted-foreground mb-8">
            Review your submission and generate your experience guide
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <span className="font-medium">Areas Created</span>
              <span className="font-semibold">{readiness?.areas_count || 0}</span>
            </div>
          </div>

          {readiness?.areas_completion && readiness.areas_completion.length > 0 && (
            <div className="mb-8">
              <h3 className="font-semibold mb-4">Area Completion Status</h3>
              <div className="space-y-3">
                {readiness.areas_completion.map((area, index) => (
                  <div key={index} className="bg-secondary/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{area.area_name}</span>
                      <span className={`text-sm font-semibold ${area.completed >= 7 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {area.completed}/{area.required}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${area.completed >= 7 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                        style={{ width: `${(area.completed / area.required) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {readiness?.issues && readiness.issues.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-rose-50 border border-rose-200 rounded-lg p-6 mb-8"
            >
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-rose-900 mb-2">Requirements Not Met</h3>
                  <p className="text-sm text-rose-700 mb-4">
                    Please complete the following before submitting:
                  </p>
                  <ul className="space-y-2">
                    {readiness.issues.map((issue, index) => (
                      <li key={index} className="text-sm text-rose-700 flex items-start gap-2">
                        <span className="text-rose-400">•</span>
                        <span>{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-8"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-emerald-900 mb-2">Ready to Submit</h3>
                  <p className="text-sm text-emerald-700">
                    All requirements have been met. You can now generate your experience guide.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Route Builder Section - Optional */}
          {readiness?.is_ready && !showRouteBuilder && (
            <div className="border border-blue-200 bg-blue-50 rounded-lg p-5 mb-6">
              <div className="flex items-start gap-3">
                <Navigation className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2">Define Custom Routes (Optional)</h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Would you like to define how visitors navigate to each area? 
                    If skipped, default routes will be generated automatically.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRouteBuilder(true)}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <Navigation className="w-4 h-4 mr-1" />
                      Define Routes
                    </Button>
                    {customRoutes.length > 0 && (
                      <span className="text-sm text-blue-600 flex items-center">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        {customRoutes.length} route(s) defined
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Design Brief Section */}
          {readiness?.is_ready && !showRouteBuilder && (
            <div className="mb-8">
              <h3 className="font-semibold mb-4">Design Brief (Optional)</h3>
              <div className="bg-secondary/30 rounded-lg p-4">
                <label className="text-sm font-medium block mb-3">
                  Brand Color
                </label>
                <div className="flex gap-3 items-center flex-wrap sm:flex-nowrap">
                  <input 
                    type="color" 
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="w-16 h-16 rounded-lg cursor-pointer border-2 border-border"
                    title="Choose your brand color"
                  />
                  <input 
                    type="text" 
                    value={brandColor}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                        setBrandColor(val);
                      }
                    }}
                    placeholder="#1e3a5f"
                    className="flex-1 min-w-[120px] px-3 py-2 rounded-lg border bg-background"
                    maxLength={7}
                  />
                  <div 
                    className="px-4 py-2 rounded-lg text-white font-medium text-sm whitespace-nowrap"
                    style={{ backgroundColor: brandColor }}
                  >
                    Preview
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  This color will be used for headers and backgrounds in your Experience Hub. Default: #1e3a5f (Deeside blue)
                </p>
              </div>
            </div>
          )}
          
          {/* Route Builder Modal/Expanded View */}
          {showRouteBuilder && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-blue-200 rounded-lg p-6 mb-6 bg-white"
            >
              <RouteBuilderStep
                areas={areas}
                customRoutes={customRoutes}
                onSaveRoutes={handleSaveRoutes}
                onSkip={() => setShowRouteBuilder(false)}
              />
            </motion.div>
          )}

          <div className="border-t border-border pt-6">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-5 mb-6">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-teal-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-teal-900 mb-2">What happens next?</h4>
                  <ol className="space-y-2 text-sm text-teal-700">
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-teal-600">1.</span>
                      <span>Your submission will be locked to preserve your assessment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-teal-600">2.</span>
                      <span>Your experience guide will be generated instantly</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-teal-600">3.</span>
                      <span>View, share, and download your guide anytime</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSubmitAndGenerate}
              disabled={!readiness?.is_ready || processing}
              className="w-full h-12 rounded-full font-medium bg-teal-600 hover:bg-teal-700"
              data-testid="generate-guide-button"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Your Guide...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Experience Guide
                </>
              )}
            </Button>
            
            <p className="text-center text-xs text-muted-foreground mt-4">
              Premium features like download and sharing are available with a subscription
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionReview;
